(ns manatee.annotation.autosaving
  (:require [clojure.set :as set]
            [manatee.annotation.app-db :as annotation-db]
            [manatee.domain.task :as task]
            [manatee.fx.debounce :as debounce]
            [manatee.fx.local-storage :as local-storage]
            [re-frame.core :as rf]
            [rothko.editor.document :as document]
            [taoensso.timbre :as log]))

(def script-prefix "script.")

(def segments-prefix "segments.")

(def segment-script-prefix "segment-script.")

(def time-markers-prefix "time-markers.")

(def playhead-prefix "playhead.")

(def offer-clearing-autosaved-items-threshold {script-prefix 20
                                               segments-prefix 20
                                               segment-script-prefix 200
                                               time-markers-prefix 20
                                               playhead-prefix 20})

(def prefixes (keys offer-clearing-autosaved-items-threshold))

(defn script-key
  [item-id]
  (str script-prefix item-id))

(defn segments-key
  [item-id]
  (str segments-prefix item-id))

(defn segment-script-key
  [item-id segment-id]
  (str segment-script-prefix item-id "_" segment-id))

(defn time-markers-key
  [item-id]
  (str time-markers-prefix item-id))

(defn playhead-key
  [item-id]
  (str playhead-prefix item-id))

(defn key-fragment
  [item-id]
  (str "." item-id))

(rf/reg-event-db
 ::set-autosaving?
 (fn set-autosaving?
   [db [_ autosaving?]]
   (annotation-db/set-autosaving? db autosaving?)))

(rf/reg-event-fx
 ::autosave
 (fn autosave
   [_ [_ key value {:keys [silent? delay]
                    :or {delay 2000}}]]
   {:dispatch-n [(when-not silent? [::set-autosaving? true])]
    ::debounce/dispatch [key
                         [::autosave* key value]
                         delay]}))

(rf/reg-event-fx
 ::autosave*
 (fn autosave*
   [_ [_ key value]]
   (if (= value ::remove)
     {::local-storage/remove-values-by-keys [key]
      :dispatch [::set-autosaving? false]}
     {::local-storage/set-value {:key key
                                 :value value
                                 :on-success [::set-autosaving? false]
                                 :on-failure [::autosave-failure key value]}})))

(rf/reg-event-fx
 ::autosave-failure
 (fn autosave-failure
   [_ [_ key value error]]
   (log/error "Unable to autosave value to local storage" {:key key
                                                           :value-length (when (coll? value) (count value))
                                                           :error error})))

(rf/reg-event-fx
 ::clear-autosaved-part
 (fn clear-autosaved-part
   [{:keys [db]} _]
   (let [key-fragments (->> (annotation-db/items-by-type db :answer)
                            (map (fn [{:keys [id]}] (key-fragment id))))]
     {:fx [[::debounce/stop key-fragments]
           [::local-storage/remove-values-by-key-fragments key-fragments]]})))

(rf/reg-event-fx
 ::clear-all-autosaved-items
 [(rf/inject-cofx ::local-storage/get-keys-by-prefixes prefixes)]
 (fn clear-all-autosaved-items
   [{keys-by-prefixes ::local-storage/get-keys-by-prefixes} _]
   {::local-storage/remove-values-by-keys (mapcat val keys-by-prefixes)}))

(rf/reg-event-fx
 ::check-if-should-offer-clearing-autosaved-items
 [(rf/inject-cofx ::local-storage/get-keys-by-prefixes prefixes)]
 (fn check-if-should-offer-clearing-autosaved-items
   [{:keys [db]
     keys-by-prefixes ::local-storage/get-keys-by-prefixes} _]
   {:db (annotation-db/set-offer-clearing-autosaved-items
         db (reduce
             (fn [_ prefix]
               (when (> (count (get keys-by-prefixes prefix))
                        (get offer-clearing-autosaved-items-threshold prefix))
                 (reduced true)))
             nil
             prefixes))}))

(rf/reg-event-db
 ::set-same-local-and-submitted-document?
 (fn same-local-and-submitted-document?
   [db [_ same?]]
   (annotation-db/set-same-local-and-submitted-document? db same?)))

(defn same-local-and-submitted-document?
  [{:keys [edit] :as _item} task-config script]
  (let [{:keys [field-name]} (task/non-scoring-field task-config)]
    (not (some-> (field-name edit)
                 (not= script)))))

(defn script-autosaving-enabled?
  [{:keys [autosaving-disabled? segment-annotation?]}]
  (and (not autosaving-disabled?)
       (not segment-annotation?)))

(rf/reg-event-fx
 ::init-script-autosaving
 (fn init-script-autosaving
   [_ [_ {:keys [item task-config]} script]]
   {:dispatch-n [[::set-autosaving? false]
                 [::set-same-local-and-submitted-document?
                  (same-local-and-submitted-document? item task-config script)]]}))

(rf/reg-event-fx
 ::autosave-script
 (fn autosave-script
   [_ [_ {:keys [item task-config]} script]]
   {:dispatch-n [[::set-same-local-and-submitted-document?
                  (same-local-and-submitted-document? item task-config script)]
                 [::autosave (script-key (:id item)) script]]}))

(rf/reg-sub
 ::autosaved-script
 (fn autosaved-script
   [db [_ item-id]]
   (annotation-db/autosaved-values db (script-key item-id))))

(defn segment-document->value
  [{:keys [script] :as document}]
  (if (document/unchanged? document) ::remove script))

(rf/reg-event-fx
 ::autosave-segments
 (fn autosave-segments
   [{:keys [db]} [_ {:keys [id] :as _item} old-segments new-segments]]
   (let [removed-segment-ids (set/difference (set (keys old-segments)) (set (keys new-segments)))
         added-segment-ids (set/difference (set (keys new-segments)) (set (keys old-segments)))
         current-segment-id (annotation-db/current-segment-id db id)
         added-or-current-segment-ids (cond-> added-segment-ids current-segment-id (conj current-segment-id))]
     {:dispatch-n
      (concat
       [[::autosave (segments-key id) new-segments]]
       (map
        (fn [segment-id]
          [::autosave (segment-script-key id segment-id) ::remove])
        removed-segment-ids)
       (map
        (fn [segment-id]
          (when-let [document (annotation-db/segment-document db id segment-id)]
            [::autosave (segment-script-key id segment-id) (segment-document->value document)]))
        added-or-current-segment-ids))})))

(rf/reg-event-fx
 ::autosave-segment-script
 (fn autosave-segment-script
   [{:keys [db]} [_ {:keys [id] :as _item} document]]
   (let [segment-id (annotation-db/current-segment-id db id)]
     {:dispatch [::autosave (segment-script-key id segment-id) (segment-document->value document)]})))

(defn autosaved-segments
  [db item-id]
  (annotation-db/autosaved-values db (segments-key item-id)))

(defn autosaved-scripts
  [db item-id]
  (->> (autosaved-segments db item-id)
       keys
       (keep (fn [segment-id]
               (when-let [script (annotation-db/autosaved-values
                                  db
                                  (segment-script-key item-id segment-id))]
                 [segment-id script])))
       (into {})))

(rf/reg-event-fx
 ::autosave-time-markers
 (fn autosave-time-markers
   [_ [_ {:keys [id] :as _item} time-markers]]
   {:dispatch [::autosave (time-markers-key id) time-markers]}))

(defn autosaved-time-markers
  [db item-id]
  (annotation-db/autosaved-values db (time-markers-key item-id)))

(rf/reg-event-fx
 ::autosave-playhead
 (fn autosave-playhead
   [_ [_ {:keys [id] :as _item} playhead]]
   {:dispatch [::autosave (playhead-key id) playhead {:silent? true
                                                      :delay 500}]}))
(rf/reg-sub
 ::autosaved-playhead
 (fn autosaved-playhead
   [db [_ item-id]]
   (annotation-db/autosaved-values db (playhead-key item-id))))
